/**
 * Canvas State Store
 *
 * Persists per-chat canvas (preview) closed-state in localStorage so a
 * canvas the user closed stays closed across message remounts and reloads
 * (BUG-01). Keyed by chatId.
 *
 * Primary responsibilities:
 * - Read/write whether the user closed the canvas for a given chat
 * - Track the last-closed timestamp for potential UX heuristics
 *
 * Dependencies:
 * - Browser localStorage (no-ops on the server)
 */

const STORAGE_KEY = 'losi:canvas-state:v1'

export type CanvasEntry = {
  // True when the user explicitly closed the canvas for this chat.
  closed: boolean
  lastClosed: number
}

export type CanvasStateStore = Record<string, CanvasEntry>

// --------------

function isBrowser() {
  return typeof window !== 'undefined' && !!window.localStorage
}

function readStore(): CanvasStateStore {
  if (!isBrowser()) return {}
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw) as unknown
    return parsed && typeof parsed === 'object' ? (parsed as CanvasStateStore) : {}
  } catch {
    return {}
  }
}

function writeStore(store: CanvasStateStore) {
  if (!isBrowser()) return
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(store))
  } catch {
    // Storage full or unavailable — best effort only.
  }
}

// --------------

export function isCanvasClosedForChat(chatId: string | null | undefined): boolean {
  if (!chatId) return false
  return readStore()[chatId]?.closed === true
}

// --------------

export function setCanvasClosedForChat(
  chatId: string | null | undefined,
  closed: boolean
): void {
  if (!chatId) return
  const store = readStore()

  if (closed) {
    store[chatId] = { closed: true, lastClosed: Date.now() }
  } else if (store[chatId]) {
    // Reopening clears the persisted closed flag for this chat.
    delete store[chatId]
  } else {
    return
  }

  writeStore(store)
}
